import { Fetcher } from '@this/src/util';
/* eslint-disable max-lines */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import _ from 'lodash';
import { getColor } from '@this/shared/ui/theme';
import styled from 'styled-components';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Textarea } from '@this/components/shared/ui/inputs/textarea';
import { RadioGroup, Radio } from '@this/shared/ui/inputs/radio';
import ContentBody, { ContentBodyIn } from '@this/components/shared/atoms/content_body';
import { Link } from '@this/components/shared/ui/navigations/link';
import TranslateIgnoreText from '@this/components/shared/text/translate_ignore_text';
import ContentHeader from '@this/components/shared/content_header/content_header';
import { Loading } from '@this/components/shared/ui/feedbacks/loading';
import { Alert } from '@this/components/shared/ui/feedbacks/alert';
import type { ProjectShareArgs } from '@this/domain/project/project_share';
import type { ChargingDepartmentShareArgs } from '@this/domain/department/charging_department_share';
import type { ExpensesAccountTypeJson } from '@this/domain/expenses/expenses_account_type';
import moment from '@this/lib/moment';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import Trip, { convertTripResponseToArgs } from '@this/domain/trip/trip2';
import { lighten, Table, TableBody, TableHead, TableRow } from '@material-ui/core';
import { media } from '@this/shared/atoms/media';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { InfoOutlined } from '@material-ui/icons';
import TripRuleSuccessSection from '@this/components/reserve_trip/reserve_confirm/routings/input_customer_information/trip_rule_section/trip_rule_success_section';
import {
  StyledDescriptionButton,
  StyledReserveErrorAlert,
  StyledReserveSuccessAlert,
  StyledAlertTitle
} from '@this/components/shared/reserve_ui_components';
import type TripRuleError from '@this/domain/trip/trip_rule_error';
import TripRuleErrorSection from '@this/components/reserve_trip/reserve_confirm/routings/input_customer_information/trip_rule_section/trip_rule_error_section';
import TripDuplicationSection from '@this/components/reserve_trip/reserve_confirm/routings/input_customer_information/trip_duplication_section/trip_duplication_section';
import { Flex } from '@this/shared/ui/layout/flex';
import TripRuleDistanceTimeInfo from '@this/domain/trip/trip_rule_distance_time_info';
import { reportError } from '@this/lib/bugsnag';
import type TravelerInformation from '@this/src/domain/traveler_information';
import type OrderItem from '@this/src/domain/order_item2';
import type NonOrderItem from '@this/src/domain/non_order_item2';
import CoreAppContext from '@this/components/core_app/core_app_context';
import Tooltip from '@this/shared/tooltip/tooltip';
import type { TripDuplicationResponseArgs } from '@this/src/domain/trip_duplication';
import TripDuplication, { convertTripDuplicationResponseToArgs } from '@this/src/domain/trip_duplication';
import InAdvanceApplicationTripDetail from './in_advance_application_trip_detail';
import NormalContent from './normal_content';
import ContentForNonOrderItem from './content_for_non_order_item';
import ChargingShareContent from './charging_share_content';

const utils = require('@this/src/utils');

interface Props {
  approved?: boolean;
}

interface DomesticAirTicketInfo {
  status: 'success' | 'invalid' | 'loading';
  priceDiff: number;
  transportVacancies: {
    transportId: number;
    price: number;
    adultprice: number;
    priceDiff: number;
    seatnum: string;
    occupied: boolean;
  }[];
}

interface DomesticAirTicketInfoResult {
  status: 'success' | 'invalid' | 'loading';
  price_diff: number;
  transport_vacancies: {
    transport_id: number;
    price: number;
    adultprice: number;
    price_diff: number;
    seatnum: string;
    occupied: boolean;
  }[];
}

interface FetchDetailTripResult {
  trip: TripResponseArgs;
  show_fee: boolean;
  hotel_error: boolean;
  transport_error: boolean;
  shinkansen_too_late_error: boolean;
  shinkansen_deadline: string | null;
  domestic_air_too_late_error: boolean;
  domestic_air_deadline: string | null;
  domestic_air_check_ticket: boolean;
  foreign_air_too_late_error: boolean;
  foreign_air_deadline: string | null;
  package_too_late_error: boolean;
  package_deadline: string | null;
  railway_package_too_late_error: boolean;
  railway_package_deadline: string | null;
  rental_car_too_late_error: boolean;
  rental_car_deadline: string | null;
  hotel_too_late_error: boolean;
  hotel_deadline: string | null;
  car_type_options: any[];
  budget_info: any;
  approve_items: string;
  shinkansen_address_info: any;
  projects: object[];
  project_shares: ProjectShareArgs[];
  departments: object[];
  charging_department_shares: ChargingDepartmentShareArgs[];
  expenses_account_types: ExpensesAccountTypeJson[];
  expenses_account_type_available: boolean;
  trip_distance_and_time_info: TripRuleDistanceTimeInfo[];
  trip_duplications: TripDuplicationResponseArgs[];
  use_slack_app: boolean;
  user_slack_connected: boolean;
}

const ApprovalButtons: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
  return (
    <div style={{ display: 'flex' }} className={className}>
      {children}
    </div>
  );
};

const InAdvanceApplicationDetail: React.FC<Props> = ({ approved }) => {
  const { serviceId, user } = useContext(CoreAppContext);
  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hotelError, setHotelError] = useState(false);
  const [transportError, setTransportError] = useState(false);
  const [shinkansenTooLateError, setShinkansenTooLateError] = useState(false);
  const [shinkansenDeadline, setShinkansenDeadline] = useState<string | null>(null);
  const [domesticAirTooLateError, setDomesticAirTooLateError] = useState(false);
  const [domesticAirDeadline, setDomesticAirDeadline] = useState<string | null>(null);
  const [domesticAirCheckTicket, setDomesticAirCheckTicket] = useState(false);
  const [foreignAirTooLateError, setForeignAirTooLateError] = useState(false);
  const [foreignAirDeadline, setForeignAirDeadline] = useState<string | null>(null);
  const [packageTooLateError, setPackageTooLateError] = useState(false);
  const [packageDeadline, setPackageDeadline] = useState<string | null>(null);
  const [railwayPackageTooLateError, setRailwayPackageTooLateError] = useState(false);
  const [railwayPackageDeadline, setRailwayPackageDeadline] = useState<string | null>(null);
  const [rentalCarTooLateError, setRentalCarTooLateError] = useState(false);
  const [rentalCarDeadline, setRentalCarDeadline] = useState<string | null>(null);
  const [hotelTooLateError, setHotelTooLateError] = useState(false);
  const [hotelDeadline, setHotelDeadline] = useState<string | null>(null);
  const [budgetInfo, setBudgetInfo] = useState<any>({});
  const [currentButtonArea, setCurrentButtonArea] = useState<string | null>(null);
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [reason, setReason] = useState('');
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve');
  const [approveItems, setApproveItems] = useState([]);
  const [shinkansenAddressInfo, setShinkansenAddressInfo] = useState<any>({});
  const [expensesAccountTypeAvailable, setExpensesAccountTypeAvailable] = useState(false);
  const [domesticAirTicketInfo, setDomesticAirTicketInfo] = useState<DomesticAirTicketInfo>({
    status: 'success',
    priceDiff: 0,
    transportVacancies: []
  });
  const [ruleDistanceTimeInfo, setRuleDistanceTimeInfo] = useState<TripRuleDistanceTimeInfo[]>([]);
  const [tripDuplications, setTripDuplications] = useState<TripDuplication[]>([]);
  const [showAlert, setShowAlert] = useState(false);
  const [useSlackApp, setUseSlackApp] = useState(false);
  const [userSlackConnected, setUserSlackConnected] = useState(false);
  const [slackNotificationDismissed, setSlackNotificationDismissed] = useState(false);

  useEffect(() => {
    switch (trip?.currentApprovalStatus) {
      case 1:
        setCurrentButtonArea('approved');
        break;
      case 2:
        setCurrentButtonArea('rejected');
        break;
      default:
        setCurrentButtonArea(approved ? 'applied' : 'normal');
        break;
    }
  }, [trip, approved]);

  const fetchInitialData = useCallback(async () => {
    window.scrollTo(0, 0);
    const id = _.last(location.href.split('/'));
    try {
      setLoading(true);

      const url = `/application/${approved ? 'approved' : 'in_advance'}/${id}.json`;
      const result = await Fetcher.get<FetchDetailTripResult>(url);
      setTrip(new Trip(convertTripResponseToArgs(result.trip, result.show_fee)));
      setHotelError(result.hotel_error);
      setTransportError(result.transport_error);
      setShinkansenTooLateError(result.shinkansen_too_late_error);
      setShinkansenDeadline(result.shinkansen_deadline);
      setDomesticAirTooLateError(result.domestic_air_too_late_error);
      setDomesticAirDeadline(result.domestic_air_deadline);
      setDomesticAirCheckTicket(result.domestic_air_check_ticket);
      setForeignAirTooLateError(result.foreign_air_too_late_error);
      setForeignAirDeadline(result.foreign_air_deadline);
      setPackageTooLateError(result.package_too_late_error);
      setPackageDeadline(result.package_deadline);
      setRailwayPackageTooLateError(result.railway_package_too_late_error);
      setRailwayPackageDeadline(result.railway_package_deadline);
      setRentalCarTooLateError(result.rental_car_too_late_error);
      setRentalCarDeadline(result.rental_car_deadline);
      setHotelTooLateError(result.hotel_too_late_error);
      setHotelDeadline(result.hotel_deadline);
      setBudgetInfo(result.budget_info);
      setApproveItems(JSON.parse(result.approve_items));
      setShinkansenAddressInfo(result.shinkansen_address_info);
      setExpensesAccountTypeAvailable(result.expenses_account_type_available);
      setRuleDistanceTimeInfo(result.trip_distance_and_time_info.map(info => new TripRuleDistanceTimeInfo(info)));
      setLoading(false);
      setDomesticAirTicketInfo({
        status: result.domestic_air_check_ticket ? 'loading' : 'success',
        priceDiff: 0,
        transportVacancies: []
      });
      setTripDuplications(
        result.trip_duplications.map(raw => new TripDuplication(convertTripDuplicationResponseToArgs(raw)))
      );
      setUseSlackApp(result.use_slack_app);
      setUserSlackConnected(result.user_slack_connected);
      setSlackNotificationDismissed(localStorage.getItem('slackNotificationDismissed') === 'true');
    } catch (error: any) {
      reportError(error); // 調査の目的で一時的にerrorをBugsnagに通知する
      console.error(error);
      let errorMessage = '通信に失敗しました。しばらくしてから再度お試しください。';
      if (error.response?.data) {
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (_.includes(error.response.data.errors, 'invalid approver')) {
          errorMessage =
            'こちらの申請情報のアクセス権がありません。\nサービスにログインしている、もしくは、ご自身がその申請情報の承認者として登録されているかご確認ください。';
        }
      }
      setLoading(false);
      setError(errorMessage);
    }
  }, [approved]);

  const fetchDomesticAirTicket = useCallback(async () => {
    if (!trip?.id) return;

    try {
      const ticketResult = await Fetcher.get<DomesticAirTicketInfoResult>(
        `/application/in_advance/${trip.id}/check_domestic_air_ticket.json`
      );
      setDomesticAirTicketInfo({
        status: ticketResult.status,
        priceDiff: ticketResult.price_diff,
        transportVacancies: ticketResult.transport_vacancies.map(tv => ({
          transportId: tv.transport_id,
          price: tv.price,
          adultprice: tv.adultprice,
          priceDiff: tv.price_diff,
          seatnum: tv.seatnum,
          occupied: tv.occupied
        }))
      });
    } catch {
      setDomesticAirTicketInfo({
        status: 'success',
        priceDiff: 0,
        transportVacancies: []
      });
    }
  }, [trip?.id]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // 国内航空券の価格チェック用の別のuseEffect
  useEffect(() => {
    if (domesticAirCheckTicket && trip?.id) {
      fetchDomesticAirTicket();
    }
  }, [domesticAirCheckTicket, trip?.id, fetchDomesticAirTicket]);

  const approvalErrorMessage = () => {
    return `申し訳ございません。ご予約を受け付けることができませんでした。

    時間を空けてもう一度お試しいただき、改善されない場合は
    下記お問い合わせフォームよりご連絡ください。

    https://${location.hostname}/inquiries/new`;
  };

  const handleSubmitApproval = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setApprovalSubmitting(true);
    setApprovalError(null);
    Fetcher.put<{ approve_reason: string }>(`/application/in_advance/${trip?.id}/approve`, {
      approve_reason: reason
    }).then(
      res => {
        setApprovalSubmitting(false);
        setReason(res.approve_reason);
        setCurrentButtonArea('approved');
      },
      error => {
        const errors = error.response?.data?.errors;
        const errorMsg =
          error.response?.data?.error ??
          (Array.isArray(errors) ? errors.join('\n') : errors) ??
          approvalErrorMessage();
        setApprovalSubmitting(false);
        setApprovalError(errorMsg);
      }
    );
  };

  const handleSubmitRejection = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (reason.length === 0) {
      setRejectError('却下する理由を入力してください');
    } else {
      setRejectSubmitting(true);
      setRejectError(null);
      Fetcher.put<{ reject_reason: string }>(`/application/in_advance/${trip?.id}/reject`, {
        reject_reason: reason
      }).then(
        res => {
          setRejectSubmitting(false);
          setReason(res.reject_reason);
          setCurrentButtonArea('rejected');
        },
        error => {
          const errors = error.response?.data?.errors;
          setRejectSubmitting(false);
          setRejectError(
            error.response?.data?.error ??
              (Array.isArray(errors) ? errors.join('\n') : errors) ??
              '通信に失敗しました。しばらくしてから再度お試しください。'
          );
        }
      );
    }
  };

  const handleSubmit = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (approvalAction === 'approve') {
      handleSubmitApproval(e);
    } else {
      handleSubmitRejection(e);
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    setReason(e.target.value);
  };

  const monthBudgetRate = () => {
    return (
      Math.round((utils.dig(budgetInfo, 'this_month_cost') / utils.dig(budgetInfo, 'monthly_budget')) * 100 * 10) /
      10
    );
  };

  const termBudgetRate = () => {
    return (
      Math.round(
        (utils.dig(budgetInfo, 'this_term_cost') / (utils.dig(budgetInfo, 'monthly_budget') * 12)) * 100 * 10
      ) / 10
    );
  };

  const handleShowAlert = (value: boolean) => {
    setShowAlert(value);
  };

  const handleDismissSlackNotification = () => {
    localStorage.setItem('slackNotificationDismissed', 'true');
    setSlackNotificationDismissed(true);
  };

  const isApproved = approved === undefined ? currentButtonArea === 'approved' : approved;

  try {
    return (
      <ApplicationDetailWrap>
        <ApplicationDetail>
          <ApplicationDetailIn>
            <SectionHeaderTitle>
              <ContentHeader
                title="出張の事前申請"
                backButton
                customTitlePosition
                customWrapPadding="0"
                customFlexBasis="200px"
              />
            </SectionHeaderTitle>

            <Body>
              <BodyIn>
                {loading ? (
                  <Loading />
                ) : error ? (
                  <Error>{error}</Error>
                ) : trip ? (
                  <>
                    {useSlackApp && !userSlackConnected && !slackNotificationDismissed && (
                      <Alert
                        variant="info"
                        title="Slack連携のお知らせ"
                        onClose={handleDismissSlackNotification}
                        style={{ marginBottom: '20px' }}
                      >
                        <div style={{ marginBottom: '8px' }}>
                          Slack連携を行うと、承認依頼をSlackで受け取ることができます。
                        </div>
                        <Button onClick={() => window.open('/slackapp/ai_travel/start_slack_auth', '_blank')}>
                          Slackと接続する
                        </Button>
                      </Alert>
                    )}
                    {tripDuplications.length ? (
                      <>
                        {tripDuplications.map((duplication, i) => (
                          <TripDuplicationSection
                            key={`trip_duplication_${duplication.tripId}_${duplication.userName}`}
                            tripDuplication={duplication}
                            apiProps={{
                              sendLog: i < 1 && !isApproved,
                              searchQueryId: trip.searchQueryId,
                              tripId: trip.id,
                              path: `/application/in_advance/${trip.id}`,
                              tripDuplications
                            }}
                          />
                        ))}
                      </>
                    ) : null}
                    <div>下記、出張を申請します。</div>
                    <OutlineBox>
                      <SectionApplication>
                        <SectionApplicationInfo>
                          <InfoSection>
                            <ApplicationInfoLabel>旅程番号</ApplicationInfoLabel>
                            <ApplicationInfoText data-wovn-ignore>{trip.id}</ApplicationInfoText>
                          </InfoSection>
                          <InfoSection>
                            <ApplicationInfoLabel>申請者</ApplicationInfoLabel>
                            <ApplicationInfoText data-wovn-ignore>{trip.user?.name}</ApplicationInfoText>
                          </InfoSection>
                          {trip.travelerInformations.map((info, n) => (
                            <InfoSection key={n}>
                              <ApplicationInfoLabel>出張者{`${n + 1}`}</ApplicationInfoLabel>
                              <ApplicationInfoText data-wovn-ignore>{info.travelerName}</ApplicationInfoText>
                            </InfoSection>
                          ))}
                          {trip.inAdvanceApprovals.map(aa => (
                            <InfoSection key={aa.id}>
                              {(aa.isApproved || aa.isRejected) && (
                                <>
                                  <ApplicationInfoLabel>{`${aa.approveStage}次承認者`}</ApplicationInfoLabel>
                                  <ApplicationInfoText>{`${
                                    aa.approver?.name || 'AI Travel'
                                  }`}</ApplicationInfoText>
                                </>
                              )}
                            </InfoSection>
                          ))}
                          {trip.nextApproval && (
                            <InfoSection>
                              <ApplicationInfoLabel>次の承認担当者</ApplicationInfoLabel>
                              <ApplicationInfoText data-wovn-ignore>
                                {`${trip.nextApproval.inAdvanceApproverCandidates
                                  .map(approver => approver.approverCandidate.name)
                                  .join(', ')}`}
                              </ApplicationInfoText>
                            </InfoSection>
                          )}

                          <InfoSection>
                            <ApplicationInfoLabel>出張期間</ApplicationInfoLabel>
                            <ApplicationInfoText data-wovn-ignore>{trip.dateRange}</ApplicationInfoText>
                          </InfoSection>
                          <InfoSection>
                            <ApplicationInfoLabel>出張人数</ApplicationInfoLabel>
                            <ApplicationInfoText data-wovn-ignore>{trip.peoplenum}名</ApplicationInfoText>
                          </InfoSection>
                          <InfoSection>
                            <ApplicationInfoLabel>申請日</ApplicationInfoLabel>
                            <ApplicationInfoText data-wovn-ignore>
                              {trip.createdAt.format('YYYY/MM/DD')}
                            </ApplicationInfoText>
                          </InfoSection>
                          {trip.flightApproveExpiredAt !== null && (
                            <InfoSection>
                              <ApplicationInfoLabel>承認期限</ApplicationInfoLabel>
                              <ApplicationInfoText data-wovn-ignore>
                                {trip.flightApproveExpiredAt.format('YYYY年MM月DD日HH時mm分')}
                              </ApplicationInfoText>
                            </InfoSection>
                          )}
                          <InfoSection>
                            <ApplicationInfoLabel>出張先</ApplicationInfoLabel>
                            <ApplicationInfoText data-wovn-ignore>{trip.finalDestination}</ApplicationInfoText>
                          </InfoSection>
                          <InfoSection>
                            <ApplicationInfoLabel>出張の目的</ApplicationInfoLabel>
                            <ApplicationInfoText data-wovn-ignore>{trip.purpose}</ApplicationInfoText>
                          </InfoSection>
                          {trip.internalNumber && (
                            <InfoSection>
                              <ApplicationInfoLabel>社内管理番号</ApplicationInfoLabel>
                              <ApplicationInfoText data-wovn-ignore>{trip.internalNumber}</ApplicationInfoText>
                            </InfoSection>
                          )}

                          {/* custom申請項目 */}
                          {approveItems !== null &&
                            approveItems.map((d: any, index: number) => {
                              if (d.dataType === 'label') {
                                return <React.Fragment key={`label${index.toString()}`} />;
                              }
                              return (
                                <InfoSection key={d.id}>
                                  <ApplicationInfoLabel>{d.userDisplayName}</ApplicationInfoLabel>
                                  {d.dataType !== 'calendar' &&
                                    (d.dataType !== 'file' ? (
                                      <ApplicationInfoText>
                                        {d.valueCode ? (
                                          <TranslateIgnoreText text={`${d.valueCode}：${d.value}`} />
                                        ) : (
                                          <TranslateIgnoreText text={d.value} />
                                        )}
                                      </ApplicationInfoText>
                                    ) : (
                                      <ApplicationInfoText>
                                        {d.filePath ? (
                                          <a
                                            href={`/trips/${trip?.id}/approve_item_files/${d.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            <FileLinkIcon src="/images/clip_highlighted.png" />
                                          </a>
                                        ) : d.fileType ? (
                                          <a
                                            href={`/trips/${trip?.id}/approve_item_files_base64/${d.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            <FileLinkIcon src="/images/clip_highlighted.png" />
                                          </a>
                                        ) : null}
                                      </ApplicationInfoText>
                                    ))}
                                  {d.dataType === 'calendar' && (
                                    <ApplicationInfoText>
                                      <TranslateIgnoreText
                                        text={d.value ? moment(d.value).format('MM月DD日') : ''}
                                      />
                                    </ApplicationInfoText>
                                  )}
                                </InfoSection>
                              );
                            })}
                        </SectionApplicationInfo>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '40%'
                          }}
                        >
                          <SectionPrice>
                            <span>申請金額</span>
                            <Price>
                              {/*
                              リクエストフォームより旅程作成の申請があり、手配オペレータが旅程が組んだ後に承認が行われる場合がある。
                              この場合は、常に金額差分がない形で画面上では表現したい。
                            */}
                              {trip.arrangementRequest ? (
                                <PriceText isOld={false}>
                                  <span data-wovn-ignore>{utils.digits(trip.totalPrice)}</span>
                                  <span>円</span>
                                </PriceText>
                              ) : (
                                <>
                                  <PriceText data-wovn-ignore isOld={trip.didTotalPriceChange}>
                                    <span data-wovn-ignore>{utils.digits(trip.oldTotalPrice)}</span>
                                    <span>円</span>
                                  </PriceText>
                                  {(trip.didTotalPriceChange || domesticAirTicketInfo.priceDiff > 0) && (
                                    <>
                                      <NewPriceText>
                                        <span data-wovn-ignore>
                                          {utils.digits(trip.totalPrice + domesticAirTicketInfo.priceDiff)}
                                        </span>
                                        <span>円</span>
                                      </NewPriceText>
                                      <PriceDesc>申請時から値段が変わっています</PriceDesc>
                                    </>
                                  )}
                                </>
                              )}
                            </Price>
                          </SectionPrice>
                          {trip.totalBufferAmount > 0 && (
                            <div
                              style={{
                                backgroundColor: '#f8f9fa',
                                padding: '8px 10px',
                                fontWeight: 'bold',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderTop: '1px dashed #ddd'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: '4px' }}>値上がり許容幅</span>
                                <Tooltip type="info">
                                  <div className="select__tooltip-format">
                                    承認後、実際に商品を手配する前に値上がりが発生した場合、いくらまで値上がりを許容して手配するかを管理画面で設定いただいています。
                                  </div>
                                </Tooltip>
                              </div>
                              <div style={{ fontSize: '14px', color: '#6B7280' }}>
                                <span data-wovn-ignore>{utils.digits(trip.totalBufferAmount)}</span>
                                <span>円</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </SectionApplication>
                    </OutlineBox>

                    {trip.ruleErrors.length > 0 && (
                      <StyledReserveErrorAlert icon={<ErrorOutlineIcon />} variant="filled" severity="error">
                        <StyledAlertTitle>※{trip.ruleErrors.length}件の規程違反があります</StyledAlertTitle>
                        <Flex>
                          <div>
                            {trip.ruleErrors.map((tripRuleError, i) => (
                              <TripRuleErrorSection key={i} tripRuleError={tripRuleError} showAlert={showAlert} />
                            ))}
                          </div>
                          {trip.ruleErrors.some(
                            (rule_error: TripRuleError) => !_.isEmpty(rule_error.detail.lines)
                          ) && (
                            <StyledDescriptionButton onClick={() => handleShowAlert(!showAlert)}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="15"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="#666666"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d={showAlert ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
                              </svg>
                            </StyledDescriptionButton>
                          )}
                        </Flex>
                      </StyledReserveErrorAlert>
                    )}
                    {trip?.ruleErrors.length === 0 && ruleDistanceTimeInfo.length > 0 && (
                      <StyledReserveSuccessAlert icon={<InfoOutlined />} variant="filled" severity="info">
                        <StyledAlertTitle>※旅費規程の範囲内です</StyledAlertTitle>
                        <Flex>
                          <div>
                            {ruleDistanceTimeInfo.map((info, i) => (
                              <TripRuleSuccessSection
                                key={i}
                                tripRuleDistanceTimeInfo={info}
                                showAlert={showAlert}
                              />
                            ))}
                          </div>
                          {ruleDistanceTimeInfo[0].detail.lines?.length && (
                            <StyledDescriptionButton onClick={() => handleShowAlert(!showAlert)}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="15"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="#666666"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d={showAlert ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
                              </svg>
                            </StyledDescriptionButton>
                          )}
                        </Flex>
                      </StyledReserveSuccessAlert>
                    )}

                    {(trip.isDepartmentMappingsExist || trip.isDepartmentNonOrderItemMappingsExist) && (
                      <section>
                        <SectionTitle>費用負担部署別の金額内訳</SectionTitle>
                        <StyledTable>
                          <StyledTableHead>
                            <TableRow>
                              <Th>部署</Th>
                              <Th minWidth="40%">詳細</Th>
                              <Th minWidth="15%">区分</Th>
                              <Th minWidth="15%">出張者</Th>
                              <Th minWidth="15%">料金</Th>
                            </TableRow>
                          </StyledTableHead>
                          <TableBody>
                            {trip.order?.orderItems.map(item => (
                              <React.Fragment key={item.id}>
                                {item.travelerInformations.map((traveler, travelerIndex) => (
                                  <React.Fragment key={traveler.id ?? `${item.id}-${travelerIndex}`}>
                                    <NormalContent
                                      item={item}
                                      traveler={traveler}
                                      total={item.travelerInformations.length}
                                      type="department"
                                    />
                                    <ChargingShareContent
                                      item={item}
                                      traveler={traveler}
                                      total={item.travelerInformations.length}
                                      type="department"
                                    />
                                  </React.Fragment>
                                ))}
                              </React.Fragment>
                            ))}
                            {trip?.nonOrderItems.map(item => (
                              <React.Fragment key={item.id}>
                                {trip?.travelerInformations.map((traveler, travelerIndex) => (
                                  <React.Fragment key={traveler.id ?? `${item.id}-${travelerIndex}`}>
                                    <ContentForNonOrderItem
                                      item={item}
                                      traveler={traveler}
                                      total={trip?.travelerInformations.length || 1}
                                      type="department"
                                    />
                                  </React.Fragment>
                                ))}
                              </React.Fragment>
                            ))}
                          </TableBody>
                        </StyledTable>
                      </section>
                    )}

                    {(trip.isProjectMappingsExist || trip.isProjectNonOrderItemMappingsExist) && (
                      <section>
                        <SectionTitle>プロジェクト別の金額内訳</SectionTitle>
                        <StyledTable>
                          <StyledTableHead>
                            <TableRow>
                              <Th>項目</Th>
                              <Th minWidth="40%">詳細</Th>
                              <Th minWidth="15%">区分</Th>
                              <Th minWidth="15%">出張者</Th>
                              <Th minWidth="15%">料金</Th>
                            </TableRow>
                          </StyledTableHead>
                          <TableBody>
                            {trip.order?.orderItems.map(item => (
                              <React.Fragment key={item.id}>
                                {item.travelerInformations.map((traveler: TravelerInformation, travelerIndex) => (
                                  <React.Fragment key={traveler.id ?? `${item.id}-${travelerIndex}`}>
                                    <NormalContent
                                      item={item}
                                      traveler={traveler}
                                      total={item.travelerInformations.length}
                                      type="project"
                                    />
                                    <ChargingShareContent
                                      item={item}
                                      traveler={traveler}
                                      total={item.travelerInformations.length}
                                      type="project"
                                    />
                                  </React.Fragment>
                                ))}
                              </React.Fragment>
                            ))}
                            {trip.nonOrderItems.map((item: NonOrderItem) => (
                              <React.Fragment key={item.id}>
                                {trip.travelerInformations.map((traveler: TravelerInformation, travelerIndex) => (
                                  <React.Fragment key={traveler.id ?? `${item.id}-${travelerIndex}`}>
                                    <ContentForNonOrderItem
                                      item={item}
                                      traveler={traveler}
                                      total={trip.travelerInformations.length}
                                      type="project"
                                    />
                                  </React.Fragment>
                                ))}
                              </React.Fragment>
                            ))}
                          </TableBody>
                        </StyledTable>
                      </section>
                    )}

                    {expensesAccountTypeAvailable &&
                      (trip.isExpensesAccountTypeMappingsExist ||
                        trip.isExpensesAccountTypeNonOrderItemMappingsExist) && (
                        <section>
                          <SectionTitle>勘定科目別の金額内訳</SectionTitle>
                          <StyledTable>
                            <StyledTableHead>
                              <TableRow>
                                <Th>項目</Th>
                                <Th minWidth="40%">詳細</Th>
                                <Th minWidth="15%">区分</Th>
                                <Th minWidth="15%">出張者</Th>
                                <Th minWidth="15%">料金</Th>
                              </TableRow>
                            </StyledTableHead>
                            <TableBody>
                              {trip?.order?.orderItems.map((item: OrderItem) => (
                                <React.Fragment key={item.id}>
                                  {item.travelerInformations.map(
                                    (traveler: TravelerInformation, travelerIndex) => (
                                      <React.Fragment key={traveler.id ?? `${item.id}-${travelerIndex}`}>
                                        <NormalContent
                                          item={item}
                                          traveler={traveler}
                                          total={item.travelerInformations.length}
                                          type="account"
                                        />
                                      </React.Fragment>
                                    )
                                  )}
                                </React.Fragment>
                              ))}
                              {trip?.nonOrderItems.map((item: NonOrderItem) => (
                                <React.Fragment key={item.id}>
                                  {trip?.travelerInformations.map(
                                    (traveler: TravelerInformation, travelerIndex) => (
                                      <React.Fragment key={traveler.id ?? `${item.id}-${travelerIndex}`}>
                                        <ContentForNonOrderItem
                                          item={item}
                                          traveler={traveler}
                                          total={trip?.travelerInformations.length || 1}
                                          type="account"
                                        />
                                      </React.Fragment>
                                    )
                                  )}
                                </React.Fragment>
                              ))}
                            </TableBody>
                          </StyledTable>
                        </section>
                      )}

                    {/* 旅程の詳細 */}
                    <InAdvanceApplicationTripDetail
                      trip={trip}
                      transportVacancies={domesticAirTicketInfo.transportVacancies}
                    />
                    {utils.dig(budgetInfo, 'monthly_budget') && (
                      <>
                        <SectionBudgetHr />
                        <SectionApplication>
                          <SectionBudgetTitle>予算消化状況</SectionBudgetTitle>
                          <SectionBudgetInfo>
                            <InfoSection>
                              <BudgetInfoLabel>年次予算消化状況</BudgetInfoLabel>
                              <BudgetInfoCost data-wovn-ignore>
                                {utils.digits(utils.dig(budgetInfo, 'this_term_cost'))}
                              </BudgetInfoCost>
                              <span>円</span>
                              <span>/</span>
                              <span data-wovn-ignore>
                                {utils.digits(utils.dig(budgetInfo, 'monthly_budget') * 12)}
                              </span>
                              <span>円</span>
                              <BudgetInfoPercent costOver={termBudgetRate() > 100} data-wovn-ignore>
                                {termBudgetRate()}%
                              </BudgetInfoPercent>
                            </InfoSection>
                            <InfoSection>
                              <BudgetInfoLabel>月次予算消化状況</BudgetInfoLabel>
                              <BudgetInfoCost data-wovn-ignore>
                                {utils.digits(utils.dig(budgetInfo, 'this_month_cost'))}
                              </BudgetInfoCost>
                              <span>円</span>
                              <span>/</span>
                              <span data-wovn-ignore>{utils.digits(utils.dig(budgetInfo, 'monthly_budget'))}</span>
                              <span>円</span>
                              <BudgetInfoPercent costOver={monthBudgetRate() > 100} data-wovn-ignore>
                                {monthBudgetRate()}%
                              </BudgetInfoPercent>
                            </InfoSection>
                          </SectionBudgetInfo>
                        </SectionApplication>
                      </>
                    )}
                    {!trip?.nonOrderItems.some(item => item.isSimpleRequest) &&
                      (utils.dig(shinkansenAddressInfo, 'shinkansen_address_postcode') ||
                        utils.dig(shinkansenAddressInfo, 'shinkansen_address') ||
                        utils.dig(shinkansenAddressInfo, 'shinkansen_addressee')) && (
                        <>
                          <SectionBudgetHr />
                          <SectionApplication>
                            <SectionBudgetTitle>送付先住所</SectionBudgetTitle>
                            <SectionBudgetInfo>
                              <AddressInfoLabel>
                                <div>{utils.dig(shinkansenAddressInfo, 'shinkansen_address_postcode')}</div>
                                <div>{utils.dig(shinkansenAddressInfo, 'shinkansen_address')}</div>
                                <div>{utils.dig(shinkansenAddressInfo, 'shinkansen_addressee')}</div>
                              </AddressInfoLabel>
                            </SectionBudgetInfo>
                          </SectionApplication>
                        </>
                      )}
                    <SectionBudgetHr />
                    <SectionApplication>
                      <SectionBudgetTitle>承認フロー</SectionBudgetTitle>
                      <StatusWrap>
                        {trip.inAdvanceApprovals.map((approval, i) => (
                          <StatusItems key={approval.id}>
                            <StatusItem
                              approved={approval.status === 1}
                              prevApproved={i > 0 && trip.inAdvanceApprovals[i - 1].status === 1}
                              prevReject={
                                i > 0 &&
                                trip.inAdvanceApprovals[i - 1].status === 2 &&
                                Boolean(trip.inAdvanceApprovals[i - 1].rejectedAt)
                              }
                              first={i === 0}
                            >
                              <StatusIcon approved={approval.status === 1 || approval.status === 2}>
                                <svg
                                  viewBox="0 0 22 22"
                                  version="1.1"
                                  xmlns="http://www.w3.org/2000/svg"
                                  xmlnsXlink="http://www.w3.org/1999/xlink"
                                >
                                  <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                    <Circle
                                      approved={approval.status === 1}
                                      strokeWidth="6"
                                      cx="11"
                                      cy="11"
                                      r="8"
                                    />
                                  </g>
                                </svg>
                              </StatusIcon>
                              <StatusText>
                                <StatusColor status={approval.status}>{approval.statusName}</StatusColor>
                                {approval.inAdvanceApproverCandidates?.length > 0 &&
                                  approval.inAdvanceApproverCandidates
                                    .map(candidate => candidate?.approverCandidate?.name || '')
                                    .join('、')}
                                {approval.status === 1 && approval.approvedAt && (
                                  <ApprovalWrapper>
                                    <ApprovalAt>
                                      (対応日：{approval.approvedAt.format('YYYY/MM/DD HH:mm')})
                                    </ApprovalAt>
                                    <div>{approval.approveReason || '　'}</div>
                                  </ApprovalWrapper>
                                )}
                                {approval.status === 2 && approval.rejectedAt && (
                                  <ApprovalWrapper>
                                    <ApprovalAt>
                                      (対応日：{approval.rejectedAt.format('YYYY/MM/DD HH:mm')})
                                    </ApprovalAt>
                                  </ApprovalWrapper>
                                )}
                              </StatusText>
                            </StatusItem>
                          </StatusItems>
                        ))}
                      </StatusWrap>
                    </SectionApplication>
                    <SectionBudgetHr />

                    <ButtonArea>
                      {!isApproved && (
                        <>
                          {trip.isHotelCheckinExpired ? (
                            <ButtonAreaError>
                              ご指定のホテル・部屋タイプはチェックイン日を過ぎています。恐れ入りますが、一度却下して再申請を依頼してください。
                            </ButtonAreaError>
                          ) : hotelError ? (
                            <ButtonAreaError>
                              ご指定のホテル・部屋タイプの空きがなくなりました。恐れ入りますが、一度却下して再申請を依頼してください。
                            </ButtonAreaError>
                          ) : hotelTooLateError ? (
                            <ButtonAreaError>
                              {`ご指定のホテルの手配は${hotelDeadline}が依頼期限です。恐れ入りますが、この申請は却下してください。`}
                            </ButtonAreaError>
                          ) : null}
                          {transportError && !foreignAirTooLateError && (
                            <ButtonAreaError>
                              ご指定の交通機関の空席がなくなりました。恐れ入りますが、一度却下して再申請を依頼してください。
                            </ButtonAreaError>
                          )}
                          {shinkansenTooLateError &&
                            (utils.isTabikobo(serviceId) ? (
                              <ButtonAreaError>{`${shinkansenDeadline}が承認期限です。恐れ入りますが、この申請は却下してください。`}</ButtonAreaError>
                            ) : (
                              <ButtonAreaError>{`新幹線・特急チケットは、${shinkansenDeadline}が承認期限です。恐れ入りますが、この申請は却下してください。`}</ButtonAreaError>
                            ))}
                          {domesticAirTooLateError && (
                            <ButtonAreaError>{`国内航空券は${domesticAirDeadline}が承認期限です。恐れ入りますが、この申請は却下してください。`}</ButtonAreaError>
                          )}
                          {packageTooLateError && (
                            <ButtonAreaError>{`パッケージは${packageDeadline}が承認期限です。恐れ入りますが、この申請は却下してください。`}</ButtonAreaError>
                          )}
                          {railwayPackageTooLateError && (
                            <ButtonAreaError>{`鉄道パッケージは${railwayPackageDeadline}が承認期限です。恐れ入りますが、この申請は却下してください。`}</ButtonAreaError>
                          )}
                          {rentalCarTooLateError && (
                            <ButtonAreaError>{`レンタカーは${rentalCarDeadline}が承認期限です。恐れ入りますが、この申請は却下してください。`}</ButtonAreaError>
                          )}
                          {trip.flightTooLateError ? (
                            <ButtonAreaError>{`この航空券は${trip.flightApproveExpiredAt?.format(
                              'YYYY年MM月DD日HH時mm分'
                            )}が承認期限です。恐れ入りますが、この申請は却下してください。`}</ButtonAreaError>
                          ) : (
                            foreignAirTooLateError && (
                              <ButtonAreaError>{`この航空券は${foreignAirDeadline}が承認期限です。恐れ入りますが、この申請は却下してください。`}</ButtonAreaError>
                            )
                          )}
                          {domesticAirTicketInfo.status === 'invalid' && (
                            <ButtonAreaError>
                              ご指定の航空券が満席になりました。恐れ入りますが、一度却下して再申請を依頼してください。
                            </ButtonAreaError>
                          )}
                        </>
                      )}
                      {user && trip.isBackStageApprover(user.id) && (
                        <ButtonAreaError>
                          現在承認できません、前の承認者が承認してからアクセスしてください。
                        </ButtonAreaError>
                      )}
                      {domesticAirTicketInfo.status === 'loading' && (
                        <ButtonAreaLoading>
                          <Loading size="small" />
                          最新の価格を取得しています。しばらくお待ちください。
                        </ButtonAreaLoading>
                      )}
                      {!_.isEmpty(trip.isApproveCanceled) ? (
                        <>
                          <ButtonAreaError>取り下げられました</ButtonAreaError>
                          <CancelReason>■理由</CancelReason>
                          <p>{trip.cancelReason}</p>
                        </>
                      ) : currentButtonArea === 'normal' ? (
                        approvalSubmitting || rejectSubmitting ? (
                          <Loading />
                        ) : (
                          <>
                            <div style={{ marginBottom: '16px' }}>
                              <RadioGroup
                                layout="horizontal"
                                value={approvalAction}
                                onChange={e => {
                                  setApprovalAction(e.target.value as 'approve' | 'reject');
                                  setApprovalError(null);
                                  setRejectError(null);
                                }}
                              >
                                <Radio value="approve">承認</Radio>
                                <Radio value="reject">却下</Radio>
                              </RadioGroup>
                            </div>
                            <Confirm>
                              理由を入力してください（{approvalAction === 'approve' ? '任意' : '必須'}）
                            </Confirm>
                            <Textarea
                              value={reason}
                              onChange={e => handleReasonChange(e)}
                              placeholder={
                                approvalAction === 'approve' ? '出張を承認する理由' : '出張を却下する理由'
                              }
                              fullWidth
                              rowsMin={3}
                            />
                            {(approvalError || rejectError) && (
                              <ApprovalError>
                                {(approvalError || rejectError || '').split('\n').map(str => {
                                  if (str.includes('https://')) {
                                    return (
                                      <Link href={str} target="_blank" rel="noopener noreferrer" key={str}>
                                        {str}
                                        <br />
                                      </Link>
                                    );
                                  }
                                  return (
                                    <React.Fragment key={`text-${str}`}>
                                      {str}
                                      <br />
                                    </React.Fragment>
                                  );
                                })}
                              </ApprovalError>
                            )}
                            <ApprovalButtons className="in-advance-application-detail__button-area">
                              {(approvalError || rejectError) && (
                                <Button
                                  onClick={() => location.reload()}
                                  color="primary"
                                  style={{ marginRight: '20px', borderRadius: '50em' }}
                                >
                                  再読込
                                </Button>
                              )}
                              <Button
                                onClick={handleSubmit}
                                color={approvalAction === 'approve' ? 'primary' : 'danger'}
                                style={{ borderRadius: '50em' }}
                                disabled={
                                  (approvalAction === 'approve' &&
                                    (trip.isHotelCheckinExpired ||
                                      hotelError ||
                                      transportError ||
                                      shinkansenTooLateError ||
                                      hotelTooLateError ||
                                      domesticAirTooLateError ||
                                      packageTooLateError ||
                                      rentalCarTooLateError ||
                                      trip.flightTooLateError ||
                                      ['loading', 'invalid'].includes(domesticAirTicketInfo.status))) ||
                                  !!(user && trip.isBackStageApprover(user.id))
                                }
                              >
                                送信
                              </Button>
                            </ApprovalButtons>
                          </>
                        )
                      ) : currentButtonArea === 'approved' ? (
                        <>
                          <ButtonAreaSuccess>承認しました</ButtonAreaSuccess>
                          <CancelReason>■理由</CancelReason>
                          <p>{trip.approveReason(trip.inAdvanceApprovals.length - 1) || reason || '記入なし'}</p>
                          {trip.receivedAt && <p>最終承認日時: {moment(trip.receivedAt).format('llll')}</p>}
                        </>
                      ) : currentButtonArea === 'rejected' ? (
                        <>
                          <ButtonAreaError>却下しました</ButtonAreaError>
                          <CancelReason>■理由</CancelReason>
                          <p>{trip.rejectReason(trip.inAdvanceApprovals.length - 1) || reason}</p>
                        </>
                      ) : currentButtonArea === 'applied' ? (
                        <ButtonAreaWait>この申請は承認待ちです</ButtonAreaWait>
                      ) : null}
                    </ButtonArea>
                  </>
                ) : null}
              </BodyIn>
            </Body>
          </ApplicationDetailIn>
        </ApplicationDetail>
      </ApplicationDetailWrap>
    );
  } catch (e) {
    console.error(e);
    reportError(e);
    return null;
  }
};

const ApplicationDetailWrap = styled.div`
  min-height: 100vh;
`;

const ApplicationDetail = styled.div`
  padding: 20px 0;
`;

const ApplicationDetailIn = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  margin: 0 auto;
`;

const Body = styled(ContentBody)`
  max-width: 1150px;
`;

const BodyIn = styled(ContentBodyIn)`
  padding: 40px 60px;

  ${media.sp`
    padding: 14px;
  `}
`;

const SectionHeaderTitle = styled.div`
  max-width: 1150px;
  margin-bottom: 20px;
  width: 100%;
  min-height: 0%;
  margin-left: auto;
  margin-right: auto;
`;

const Error = styled.div`
  color: ${props => props.theme.redColor};
  white-space: pre-line;
`;

const InfoSection = styled.div`
  display: flex;
  align-items: flex-end;
`;

const Price = styled.div`
  font-size: 20px;
  font-weight: bold;

  ${media.sp`
    font-size: 14px;
  `}
`;

const SectionPrice = styled.div`
  background-color: ${props => props.theme.grayBgColorLight};
  padding: 10px;
  font-weight: bold;
  min-height: 56px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${media.sp`
    width: 100%;
  `}
`;

const PriceText = styled.div<{ isOld: boolean }>`
  ${props =>
    props.isOld &&
    `
    text-decoration: line-through;
  `}
`;

const NewPriceText = styled.div`
  color: ${props => props.theme.redColor};
`;

const PriceDesc = styled.div`
  color: ${props => props.theme.redColor};
  font-size: 10px;
  font-weight: normal;
`;

const SectionTitle = styled.h3`
  margin-top: 40px;
  margin-bottom: 5px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${props => props.theme.grayBorderColor};
`;

const ButtonArea = styled.section`
  margin-top: 40px;
`;

const ButtonAreaSuccess = styled.p`
  color: ${props => props.theme.successColor};
`;

const ButtonAreaError = styled.p`
  color: ${props => props.theme.redColor};
`;

const ButtonAreaWait = styled.p`
  color: ${props => props.theme.grayTextColor};
`;

const ButtonAreaLoading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  div {
    flex-grow: inherit;
  }
`;

const CancelReason = styled.p`
  margin-top: 10px;
`;

const Confirm = styled.p`
  font-weight: bold;
  margin-bottom: 10px;
`;

const ApprovalError = styled.p`
  color: ${props => props.theme.redColor};
`;

const FileLinkIcon = styled.img`
  width: 20px;
`;

const OutlineBox = styled.div`
  border: 1px solid ${getColor('border', 'divider')};
  padding: 20px;
  margin: 10px 0;

  ${media.sp`
     padding: 6px;
  `}
`;

const SectionApplication = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  ${media.sp`
    flex-direction: column;
  `}
`;

const SectionApplicationInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 60%;

  ${media.sp`
    width: 100%;
  `}
`;

const ApplicationInfoLabel = styled.div`
  padding: 5px 10px 5px 0;
  font-weight: bold;
  width: 180px;
`;

const ApplicationInfoText = styled.span`
  padding: 5px 0;
  word-break: break-all;
`;

const SectionBudgetHr = styled.hr`
  margin: 15px 0;
  border: 1px solid ${props => props.theme.grayBorderColor};
`;

const SectionBudgetTitle = styled.div`
  min-width: 140px;
  font-weight: bold;
`;

const SectionBudgetInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`;

const BudgetInfoLabel = styled.span`
  padding: 0 20px;
  font-weight: bold;
`;

const AddressInfoLabel = styled.span`
  padding-left: 20px;
`;

const BudgetInfoCost = styled.span`
  padding-left: 20px;
  font-size: 18px;
  font-weight: bold;

  ${media.sp`
    padding-left: 6px;
  `}
`;

const BudgetInfoPercent = styled.span<{ costOver: boolean }>`
  padding-left: 40px;
  font-weight: bold;
  color: ${props => (props.costOver ? `#DC2626` : `#AAAAAA`)};

  ${media.sp`
    padding-left: 10px;
  `}
`;

const StyledTable = styled(Table)`
  border: 1px solid ${getColor('border', 'divider')};

  ${media.sp`
    display: block;

    .spDisable {
      display: none;
    }

    .MuiTableHead-root {
      display: none;
    }

    .MuiTableBody-root {
      display: flex;
      flex-flow: column;
    }

    .MuiTableRow-root {
      display: flex;
      flex-flow: wrap;
      align-items: center;
      padding: 16px;
      gap: 8px 16px;
      border-bottom: solid 1px #eee;
    }

    .MuiTableCell-root {
      border: 0;
      padding: 0;
      width: 100%;

      &.spWrap {
        width: auto;
      }

      &:last-child {
        padding-top: 8px;
        border-top: solid 1px #eee;
      }

      &:first-child {
        padding-top: 0;
        border-top: 0;
      }
    }
  `}
`;

const StyledTableHead = styled(TableHead)`
  background-color: ${props => props.theme.grayBgColorLight};
  font-weight: bold;
  .MuiTableCell {
    &-head {
      padding: 8px 16px;
      min-width: 120px;
    }
  }

  ${media.sp`
    display: none;
  `}
`;

const Th = styled.th<{ minWidth?: string }>`
  border: 1px solid ${getColor('border', 'divider')};
  padding: 10px 16px;
  width: ${props => props.minWidth || '50px'};
  text-align: center;
`;

const StatusWrap = styled.div`
  display: flex;
  flex-flow: column;
  flex: 1;
  gap: 1.6em;
  padding: 0;
  ${media.sp`
    padding: 16px 0;
    gap: 16px;
    max-width: 350px;
  `};
`;

const StatusItems = styled.div`
  display: flex;
  flex-flow: column;
  gap: 1.6em;

  ${media.sp`
    padding: 0;
    gap: 16px;
  `};
`;

const StatusItem = styled.div<{ approved: boolean; first: boolean; prevApproved: boolean; prevReject: boolean }>`
  position: relative;
  padding-left: 29px;

  &:before {
    display: block;
    content: '';
    position: absolute;
    left: 9px;
    z-index: 0;
    border-left: solid 3px ${props => props.theme.linkColor};
    height: ${props => (props.prevApproved ? '3.6em' : '1.7em')};
    top: ${props => (props.prevApproved ? '-3.2em' : '-1.6em')};

    ${props =>
      props.prevApproved &&
      media.sp`
      height: 5.1em;
      top: -4.8em;
    `}
    ${props =>
      props.prevReject &&
      media.sp`
      height: 3.6em;
      top: -3.2em;
  `}

    ${props =>
      props.approved &&
      `
      border-color: ${lighten(props.theme.linkColor, 0.6)};
    `}
    ${props =>
      props.first &&
      `
      border-color: transparent;
    `}
  }

  ${media.sp`
    margin-bottom: 6px;
  `}
`;

const StatusIcon = styled.div<{ approved: boolean }>`
  position: absolute;
  top: 1px;
  left: 0;
  z-index: 10;
  width: 21px;
  height: 21px;
`;

const Circle = styled.circle<{ approved: boolean }>`
  stroke: ${props => props.theme.linkColor};

  ${props =>
    props.approved &&
    `
    stroke: ${lighten(props.theme.linkColor, 0.6)};
  `}
`;

const StatusText = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
`;

const COLORS = {
  gray: '#6B7280',
  orange: '#FB923C',
  green: '#10B981'
};

const getStatusColor = (status: number) => {
  switch (status) {
    case 1:
      return COLORS.green;
    case 0:
      return COLORS.orange;
    default:
      return COLORS.gray;
  }
};

const StatusColor = styled.div<{ status: number }>`
  color: ${props => getStatusColor(props.status)};
  width: 80px;
`;

const ApprovalAt = styled.div`
  color: ${props => props.theme.grayTextColor};
`;

const ApprovalWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export default InAdvanceApplicationDetail;
